import React, { useState, useEffect, useRef, createRef } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import primary from '@/assets/automation/primaryFrame.png';
import secondary from '@/assets/automation/secondaryFrame.png';
import tertiary from '@/assets/automation/tertiaryFrame.png';
import quadnary from '@/assets/automation/quadnaryFrame.png';

const features = [
  {
    title: 'Automated claim support',
    desc: 'Deliver instant claim processing that drives customer satisfaction and boosts loyalty.',
  },
  {
    title: 'AI-powered fraud monitoring',
    desc: 'Powerful AI identifies fraudulent patterns in real time, reducing chargebacks and losses.',
  },
  {
    title: 'Configurable business rules',
    desc: 'Define your own workflows, SLAs, and escalation paths to fit any team’s needs.',
  },
];

const FRAME_SCROLL = 2000;
const FRAME_SLICE = FRAME_SCROLL / 4;
const frames = [primary, secondary, tertiary, quadnary];

const Automation: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rawY, setRawY] = useState(0);
  const [progress, setProgress] = useState<number[]>(features.map(() => 0));
  const [frameIdx, setFrameIdx] = useState(0);
  const [prevFrameIdx, setPrevFrameIdx] = useState<number | null>(null);

  const descRefs = useRef(features.map(() => createRef<HTMLDivElement>()));
  const [descHeights, setDescHeights] = useState<number[]>(features.map(() => 0));

  const scrollSpring = useSpring(rawY / FRAME_SCROLL, { stiffness: 50, damping: 20 });
  const drawerWidth = useTransform(scrollSpring, [0, 0.5, 1], ['90%', '100%', '100%']);

  // Preload images to prevent flashing
  useEffect(() => {
    frames.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Measure description heights on mount
  useEffect(() => {
    setDescHeights(
      descRefs.current.map((ref) => ref.current?.scrollHeight ?? 0)
    );
  }, []);

  // Scroll handler logic
  useEffect(() => {
    const onScroll = () => {
      if (!containerRef.current) return;
      const startY = containerRef.current.offsetTop;
      const raw = window.scrollY - startY;
      const clamped = Math.min(Math.max(raw, 0), FRAME_SCROLL);
      setRawY(clamped);

      const newProgress = features.map((_, i) => {
        const s = FRAME_SLICE * i;
        const e = s + FRAME_SLICE;
        if (clamped <= s) return 0;
        if (clamped >= e) return 1;
        return (clamped - s) / FRAME_SLICE;
      });
      setProgress(newProgress);

      let newFrameIdx = 0;
      for (let i = 0; i < newProgress.length; i++) {
        if (newProgress[i] > 0) {
          newFrameIdx = i + 1;
        }
      }
      setFrameIdx((prev) => {
        if (prev !== newFrameIdx) {
          setPrevFrameIdx(prev);
        }
        return newFrameIdx;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ height: `calc(100vh + ${FRAME_SCROLL}px)` }}
      className="tw-relative"
    >
      <section className="tw-sticky tw-top-0 tw-min-h-screen tw-bg-black tw-py-16 tw-pb-48 tw-px-6 sm:tw-px-12 tw-overflow-hidden">
        <div className="tw-max-w-7xl tw-mx-auto tw-grid md:tw-grid-cols-2 tw-gap-12 tw-h-full">

          {/* LEFT PANEL */}
          <motion.div
            style={{ width: drawerWidth }}
            className="tw-flex tw-flex-col tw-justify-center"
          >
            <p className="tw-text-xs tw-uppercase tw-text-gray-400 tw-tracking-widest tw-mb-2">
              Post-purchase automation
            </p>
            <h2 className="tw-text-4xl sm:tw-text-5xl tw-font-extrabold tw-text-white tw-leading-snug tw-mb-8">
              Cut costs,<br />
              save time,<br />
              <span className="tw-text-[#00C5FB]">boost LTV.</span>
            </h2>

            <ul className="tw-flex tw-flex-col tw-space-y-6 tw-mb-10">
              {features.map((f, i) => (
                <div key={i} className="tw-flex">
                  <div
                    className="tw-w-[8px] tw-bg-gray-600 tw-relative tw-rounded"
                    style={{ height: '100%' }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        background: '#06b6d4',
                        height: `${progress[i] * 100}%`
                      }}
                      className="tw-rounded"
                    />
                  </div>

                  <div className="tw-flex-1 tw-flex tw-flex-col tw-pl-4 tw-my-2">
                    <h3 className="tw-text-3xl tw-w-7/12 tw-text-white tw-font-semibold">
                      {f.title}
                    </h3>
                    <div
                      style={{
                        height: descHeights[i] * progress[i],
                        overflow: 'hidden'
                      }}
                    >
                      <div ref={descRefs.current[i]}>
                        <p className="tw-text-gray-300 tw-w-3/4 tw-mt-1 tw-pb-2">
                          {f.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </ul>

            <Link to="/schedule-demo" className="tw-bg-[#00C5FB] tw-text-black tw-w-7/12 tw-font-medium tw-py-3 tw-px-8 tw-rounded-full hover:tw-bg-white hover:tw-text-cyan-600 tw-transition-colors tw-duration-700 tw-text-center">
              Get Started
            </Link>
          </motion.div>

          {/* RIGHT PANEL */}
          <div className=" tw-hidden md:tw-relative md:tw-flex md:tw-items-center md:tw-justify-center" />


          <div className="tw-hidden md:tw-block  tw-w-full tw-max-w-[800px] sm:tw-max-w-[600px] md:tw-max-w-[900px] lg:tw-max-w-[1000px] tw-pointer-events-none tw-absolute -tw-top-8 -tw-right-44 -tw-z-10">
            {/* Previous frame (fade out only) */}
            {prevFrameIdx !== null && frameIdx !== prevFrameIdx && (
              <motion.img
                key={`prev-${prevFrameIdx}`}
                src={frames[prevFrameIdx]}
                alt="Previous frame"
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
                onAnimationComplete={() => setPrevFrameIdx(null)}
                className="tw-absolute tw-inset-0 tw-object-contain tw-z-0 tw-w-full tw-h-auto"
                style={{ rotate: '15deg' }}
              />
            )}

            {/* Current frame (fade in only) */}
            <motion.img
              key={`curr-${frameIdx}`}
              src={frames[frameIdx]}
              alt="Current frame"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              style={{ rotate: '15deg' }}
              className="tw-absolute tw-inset-0 tw-object-contain tw-z-20 tw-w-full tw-h-auto"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Automation;
